import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const base = {
    viewBox: "0 0 24 24",
    width: 22,
    height: 22,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.5,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
};

/** Object along path — path with a marker */
export function IconObjectAlongPath(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 18c3-6 5-12 16-10" />
            <circle cx="14" cy="10" r="2" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Object fill pack — filled region with grid dots */
export function IconObjectFillPack(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="5" y="5" width="14" height="14" rx="2" />
            <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="15" cy="9" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="12" cy="15" r="1.2" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Primitive points — cube wire with projected points */
export function IconPrimitivePoints(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M7 7l6-3 6 3-6 3-6-3z" />
            <path d="M7 7v8l6 3 6-3V7" />
            <circle cx="7" cy="7" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="13" cy="4" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="19" cy="7" r="1.2" fill="currentColor" stroke="none" />
            <circle cx="13" cy="10" r="1.2" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Arc connections — two nodes, curved edge */
export function IconArcConnections(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <circle cx="6" cy="12" r="2.5" />
            <circle cx="18" cy="12" r="2.5" />
            <path d="M8.3 10.5Q12 6 15.7 10.5" />
        </svg>
    );
}

/** Rect node connections — squares with diagonals, straight link */
export function IconRectNodeConnections(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 8h6v6H4zM4 8l6 6M10 8L4 14" />
            <path d="M14 8h6v6h-6zM14 8l6 6M20 8l-6 6" />
            <path d="M10 11h4" />
        </svg>
    );
}

/** Text noise fill — letter with stipple */
export function IconTextNoiseFill(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M12 4v16M8 8h8" strokeWidth={1.25} />
            <circle cx="9" cy="13" r="0.6" fill="currentColor" stroke="none" />
            <circle cx="12" cy="14" r="0.6" fill="currentColor" stroke="none" />
            <circle cx="15" cy="13" r="0.6" fill="currentColor" stroke="none" />
            <circle cx="10.5" cy="16" r="0.6" fill="currentColor" stroke="none" />
            <circle cx="13.5" cy="16" r="0.6" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Point distortion — rectangle with vertex tugged */
export function IconPointDistortion(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 6h16v12H4z" />
            <circle cx="18" cy="8" r="2" fill="currentColor" stroke="none" />
            <path d="M16 8l-4 2" strokeDasharray="2 2" />
        </svg>
    );
}

/** Grid editor */
export function IconGridEditor(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 4h16v16H4zM4 10h16M4 16h16M10 4v16M16 4v16" />
        </svg>
    );
}

/** Trait connections — small network */
export function IconTraitConnections(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <circle cx="6" cy="8" r="1.8" />
            <circle cx="18" cy="6" r="1.8" />
            <circle cx="14" cy="16" r="1.8" />
            <path d="M7.5 8.8l5 5.5M16.5 7.2l-1.5 7.5M8.5 9.5l4.5 5" />
        </svg>
    );
}

/** Channel split — RGB overlapping (additive blend read at a glance) */
export function IconChannelSplit(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <circle
                cx="9"
                cy="14"
                r="5.2"
                fill="#00c853"
                fillOpacity={0.72}
                stroke="none"
            />
            <circle
                cx="15"
                cy="14"
                r="5.2"
                fill="#2962ff"
                fillOpacity={0.72}
                stroke="none"
            />
            <circle
                cx="12"
                cy="8.5"
                r="5.2"
                fill="#ff1744"
                fillOpacity={0.72}
                stroke="none"
            />
        </svg>
    );
}

/** SVG to raster */
export function IconSvgToRaster(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="5" width="10" height="10" rx="1" />
            <path d="M5 14l2.5-3 2 2.5L13 9" />
            <path d="M14 13h7v6H14z" />
            <path d="M15.5 15.5h4M15.5 17.5h4" strokeWidth={1} />
        </svg>
    );
}

/** ASCII art */
export function IconAsciiArt(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path
                d="M5 6h2M9 6h2M13 6h2M17 6h2M5 9h2M9 9h2M13 9h2M17 9h2M5 12h2M9 12h2M13 12h2M17 12h2M5 15h2M9 15h2M13 15h2M17 15h2"
                strokeWidth={1.25}
            />
        </svg>
    );
}

/** Luminance grid — checker of light / dark fills */
export function IconLuminanceGrid(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect
                x="4"
                y="4"
                width="7"
                height="7"
                fill="currentColor"
                stroke="none"
                opacity={0.2}
            />
            <rect
                x="12"
                y="4"
                width="7"
                height="7"
                fill="currentColor"
                stroke="none"
                opacity={0.55}
            />
            <rect
                x="4"
                y="12"
                width="7"
                height="7"
                fill="currentColor"
                stroke="none"
                opacity={0.85}
            />
            <rect
                x="12"
                y="12"
                width="7"
                height="7"
                fill="currentColor"
                stroke="none"
                opacity={0.35}
            />
        </svg>
    );
}

/** Font mixer */
export function IconFontMixer(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M9 18V6M6 6h6" strokeWidth={1.5} />
            <path d="M15 18l3-12h2" strokeWidth={1.35} opacity={0.85} />
        </svg>
    );
}

/** Blob field — organic blob outline */
export function IconBlobField(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M12 4c3.5 0 6 2 7 5s0 7-3 9-8 2-11-1-4-7-1-11 4-6 8-2z" />
        </svg>
    );
}

/** Flow field — curved streamlines */
export function IconFlowField(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M3 18c4-8 8-14 18-16M2 12c5-4 10-6 20-5M4 6c6 2 12 8 16 14" />
        </svg>
    );
}

/** Path agents — wiggly trail and head */
export function IconPathAgents(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 14c3-4 6-8 16-10" />
            <circle cx="17" cy="5" r="2" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Flow agents — field vectors + following dot */
export function IconFlowAgents(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M3 8c3-1 6-1 9 0M3 12h12M3 16c4 1 8 1 12-1" strokeWidth={1.2} />
            <path d="M14 6c2 2 4 5 5 9" opacity={0.85} />
            <circle cx="19" cy="15" r="2" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Domain warp — swirl */
export function IconDomainWarp(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M12 12c-4 0-6-3-6-6s3-5 6-4 5 4 4 7-4 5-8 4-7-3-6-8 5-9 10-7 9 6 7 11-8 7-13 3" />
        </svg>
    );
}

/** Displacement map — mesh warp */
export function IconDisplacementMap(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 8h16M4 12h16M4 16h16M8 4v16M12 4v16M16 4v16" strokeWidth={1} opacity={0.5} />
            <path d="M5 6c3 1 5 5 7 10s4 6 8 5" />
            <path d="M19 8c-2 2-4 6-5 10" />
        </svg>
    );
}

/** Superformula — symmetric flower */
export function IconSuperformula(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M12 3c2 3 2 5 0 8-2-3-2-5 0-8zM12 21c-2-3-2-5 0-8 2 3 2 5 0 8zM3 12c3-2 5-2 8 0-3 2-5 2-8 0zM21 12c-3 2-5 2-8 0 3-2 5-2 8 0z" />
            <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Topographic map — contour lines */
export function IconTopographicMap(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 16c3-1 5-1 8 0s6 1 8-1M5 12c2.5-0.5 5-0.5 7 0s5.5 0.5 7 0M6 8c2-0.3 4-0.3 6 0s4 0.3 6 0" />
            <path d="M10 18c1-4 2-8 2-12" strokeWidth={1} opacity={0.6} />
        </svg>
    );
}

/** Nested grid — grid cell containing a smaller grid */
export function IconNestedGrid(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="3" width="18" height="18" rx="1" />
            <path d="M3 11h18M11 3v18" />
            <rect x="13" y="13" width="6" height="6" fill="currentColor" stroke="none" opacity={0.25} />
            <path d="M13 16h6M16 13v6" strokeWidth={1} />
        </svg>
    );
}

/** Pixel stretch — linked rects with streak connectors */
export function IconPixelStretch(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="14" width="7" height="6" rx="0.5" />
            <rect x="14" y="4" width="7" height="6" rx="0.5" />
            <path
                d="M10 14 L10 11 L17 10"
                strokeWidth={1.25}
                opacity={0.85}
            />
            <path d="M10 11h7" strokeWidth={0.75} opacity={0.5} />
            <path d="M10 12.5h7" strokeWidth={0.75} opacity={0.5} />
            <path d="M10 14h7" strokeWidth={0.75} opacity={0.5} />
        </svg>
    );
}

/** Lattice deform — 3×3 mesh with one node pulled off-grid */
export function IconLatticeDeform(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            {/* Mesh: distorted rows and columns suggesting bilinear warp */}
            <path
                d="M4 4 Q10 6 14 4 T20 5"
                strokeWidth={1}
                opacity={0.7}
            />
            <path
                d="M4 12 Q9 14 14 12 T20 13"
                strokeWidth={1}
                opacity={0.7}
            />
            <path
                d="M4 20 Q10 18 14 20 T20 19"
                strokeWidth={1}
                opacity={0.7}
            />
            <path
                d="M4 4 Q6 10 4 12 T4 20"
                strokeWidth={1}
                opacity={0.7}
            />
            <path
                d="M14 4 Q12 10 14 12 T14 20"
                strokeWidth={1}
                opacity={0.7}
            />
            <path
                d="M20 5 Q18 10 20 13 T20 19"
                strokeWidth={1}
                opacity={0.7}
            />
            {/* Mesh nodes */}
            <circle cx="4" cy="4" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="14" cy="4" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="20" cy="5" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="4" cy="12" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="14" cy="12" r="1.6" fill="currentColor" stroke="none" />
            <circle cx="20" cy="13" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="4" cy="20" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="14" cy="20" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="20" cy="19" r="1.1" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Distortion grid — 3×3 grid with increasing warp per cell */
export function IconDistortionGrid(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            {/* Grid lines */}
            <path d="M3 3h18v18H3z" strokeWidth={1.25} />
            <path d="M3 10h18M3 17h18M10 3v18M17 3v18" strokeWidth={1} opacity={0.5} />
            {/* Warped paths: mild in top-left, stronger toward bottom-right */}
            <path d="M4.5 6.5c1-0.3 2 0.3 3.5 0" strokeWidth={1} opacity={0.6} />
            <path d="M11.5 6c0.8-0.5 2 0.8 3.5 0.2" strokeWidth={1} opacity={0.7} />
            <path d="M18 5.5c0.5-0.8 1-0.2 1.5 0.5" strokeWidth={1} opacity={0.8} />
            <path d="M4 13.5c1.5-1 2.5 1 3.5-0.5" strokeWidth={1} opacity={0.75} />
            <path d="M11 13c1-1.5 2.5 1.5 3.5-0.5" strokeWidth={1.2} opacity={0.85} />
            <path d="M18 12c0.5-1.5 1.5 2 1.5-0.5" strokeWidth={1.3} />
            <path d="M4 20c2-2 1.5 1.5 3.5-1" strokeWidth={1.2} opacity={0.8} />
            <path d="M11 20c1.5-2.5 2 2 3.5-1.5" strokeWidth={1.4} opacity={0.9} />
            <path d="M18 19c0.5-2 1.5 2 1.5-1.5" strokeWidth={1.5} />
        </svg>
    );
}

/** NES Glitch — controller D-pad with corrupted scanlines */
export function IconNesGlitch(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="6" width="18" height="12" rx="1.5" />
            <path d="M3 10h18M3 14h18" strokeWidth={1} opacity={0.45} />
            <rect x="6" y="8.5" width="2" height="2" fill="currentColor" stroke="none" />
            <rect x="10" y="13" width="2" height="2" fill="currentColor" stroke="none" opacity={0.65} />
            <rect x="14" y="9" width="2" height="2" fill="currentColor" stroke="none" opacity={0.85} />
            <rect x="17" y="13" width="2" height="2" fill="currentColor" stroke="none" opacity={0.5} />
        </svg>
    );
}

/** Artbrush SVG canvas — brush stroke on sheet */
export function IconArtbrushSvgCanvas(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="4" width="18" height="16" rx="1.5" strokeWidth={1.25} />
            <path
                d="M6 17c3-6 5-9 8-11 2-1.5 4-1.5 6 0 1.5 1 2 2.5 1.5 4-.5 1.5-2 2-3.5 1"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />
            <circle cx="17" cy="7" r="1.25" fill="currentColor" stroke="none" opacity={0.5} />
        </svg>
    );
}

/** Pixel SVG canvas — small pixel-art mosaic */
export function IconPixelSvgCanvas(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="3" width="18" height="18" strokeWidth={1.25} />
            <rect x="6" y="6" width="3" height="3" fill="currentColor" stroke="none" />
            <rect x="9" y="6" width="3" height="3" fill="currentColor" stroke="none" opacity={0.55} />
            <rect x="12" y="9" width="3" height="3" fill="currentColor" stroke="none" />
            <rect x="9" y="12" width="3" height="3" fill="currentColor" stroke="none" opacity={0.7} />
            <rect x="6" y="15" width="3" height="3" fill="currentColor" stroke="none" opacity={0.4} />
            <rect x="15" y="12" width="3" height="3" fill="currentColor" stroke="none" opacity={0.85} />
            <rect x="12" y="15" width="3" height="3" fill="currentColor" stroke="none" opacity={0.55} />
        </svg>
    );
}

/** Grid cell wrapper — highlighted occupied cells over grid */
export function IconGridCellWrapper(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="3" width="18" height="18" strokeWidth={1.25} />
            <path d="M3 9h18M3 15h18M9 3v18M15 3v18" strokeWidth={1} />
            <rect
                x="9"
                y="9"
                width="6"
                height="6"
                fill="currentColor"
                stroke="none"
                opacity={0.35}
            />
            <rect
                x="3"
                y="15"
                width="6"
                height="6"
                fill="currentColor"
                stroke="none"
                opacity={0.35}
            />
        </svg>
    );
}

/** Symbol generator — grid plus glyph stroke */
export function IconSymbolGenerator(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 4h16v16H4zM4 10h8M4 16h8M10 4v16" strokeWidth={1} opacity={0.45} />
            <path d="M11 7c2-1 4 0 4 2 0 1.5-1 2.5-2.5 3.2-1.6.8-2.5 1.5-2.5 3.3" />
            <path d="M11 17h5" />
        </svg>
    );
}

/** Polar grid cells — concentric rings of dots */
export function IconPolarGridCells(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            {/* outer ring */}
            <circle cx="12" cy="12" r="9" strokeWidth={1} opacity={0.3} />
            {/* mid ring */}
            <circle cx="12" cy="12" r="5.5" strokeWidth={1} opacity={0.3} />
            {/* inner ring */}
            <circle cx="12" cy="12" r="2.2" strokeWidth={1} opacity={0.3} />
            {/* dots on outer ring */}
            <circle cx="12" cy="3" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="21" cy="12" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="12" cy="21" r="1.3" fill="currentColor" stroke="none" />
            <circle cx="3" cy="12" r="1.3" fill="currentColor" stroke="none" />
            {/* dots on mid ring */}
            <circle cx="12" cy="6.5" r="1" fill="currentColor" stroke="none" opacity={0.75} />
            <circle cx="17.5" cy="12" r="1" fill="currentColor" stroke="none" opacity={0.75} />
            <circle cx="12" cy="17.5" r="1" fill="currentColor" stroke="none" opacity={0.75} />
            <circle cx="6.5" cy="12" r="1" fill="currentColor" stroke="none" opacity={0.75} />
        </svg>
    );
}

/** Voronoi — cells meeting at a vertex */
export function IconVoronoi(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M12 4L20 14H4z" opacity={0.35} />
            <path d="M4 14L12 20L20 14M12 4v16" />
            <circle cx="12" cy="14" r="1.6" fill="currentColor" stroke="none" />
        </svg>
    );
}

/** Grid cubes 3D — stacked isometric cubes with a connecting link */
export function IconGridCubes3d(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M5 7.5l3.5-2 3.5 2-3.5 2-3.5-2z" />
            <path d="M5 7.5v4l3.5 2 3.5-2v-4" />
            <path d="M13 14.5l3.5-2 3.5 2-3.5 2-3.5-2z" />
            <path d="M13 14.5v3l3.5 2 3.5-2v-3" />
            <path d="M10 10.5l4 3" opacity={0.6} />
        </svg>
    );
}

/** Silhouette extractor — 3D sphere with bold outer contour */
export function IconSilhouetteExtractor(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <circle cx="12" cy="12" r="8.5" strokeWidth={2} />
            <ellipse cx="12" cy="12" rx="4" ry="8.5" strokeWidth={0.8} opacity={0.35} />
            <line x1="3.5" y1="12" x2="20.5" y2="12" strokeWidth={0.8} opacity={0.35} />
        </svg>
    );
}

/** Volume builder 3D — a cuboid and a cylinder packed in space */
export function IconVolumeBuilder3d(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <path d="M4 8l3-1.8 3 1.8-3 1.8-3-1.8z" />
            <path d="M4 8v4.5l3 1.8 3-1.8V8" />
            <ellipse cx="16.5" cy="9" rx="2.8" ry="1.3" />
            <path d="M13.7 9v5.5a2.8 1.3 0 0 0 5.6 0V9" />
        </svg>
    );
}

/** 2D SVG diagram — flat node graph inside a frame */
export function IconSvgDiagram2D(props: IconProps) {
    return (
        <svg {...base} aria-hidden {...props}>
            <rect x="3" y="3" width="18" height="18" rx="1.5" />
            <line x1="8" y1="8" x2="16" y2="9" />
            <line x1="8" y1="8" x2="9" y2="16" />
            <line x1="16" y1="9" x2="15" y2="16" />
            <circle cx="8" cy="8" r="1.8" fill="currentColor" stroke="none" />
            <circle cx="16" cy="9" r="1.8" fill="currentColor" stroke="none" />
            <circle cx="9" cy="16" r="1.8" fill="currentColor" stroke="none" />
            <circle cx="15" cy="16" r="1.8" fill="currentColor" stroke="none" />
        </svg>
    );
}
